/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  HStack,
  Image,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Box,
  Switch,
  FormControl,
  FormLabel,
  Stack,
} from '@chakra-ui/react';
import { Labeled } from '@partiu/ui/components/molecules/Labeled';
import React, { useImperativeHandle, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
// import { InputImage } from './InputImage';
import { BenefitsSelector } from './BenefitsSelector';
// import slugify from 'slugify';
import { useQueryClient } from 'react-query';
import { DashboardHooks } from '@partiu/api/dashboard/hooks';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';
import { axiosInstance } from '@partiu/api/axios';

// Interface para o submit do formulário
export interface ModuleProductSubmit {
  name: string;
  default_product: string;
  metadata: {
    url: string;
    logo: File | string;
    category: string;
    isExternalLink: boolean;
  };
  legal_terms: boolean;
  is_external: boolean;
}

// Interface completa para os dados recebidos da API
export interface ModuleProduct {
  id?: number;
  title: string;
  url: string;
  image?: string;
  icon?: string;
  sectionTitle?: string;
  category?: string;
  is_external: boolean;
  plans?: any[];
  default_product: {
    id?: number;
  };
  active: boolean;
  name: string;
  created_at?: string;
  updated_at?: string;
  isExternal: boolean;
  metadata: {
    url: {
      value: string;
    };
    logo: {
      type: string;
      value: string | File;
    };
    category: {
      value: string;
    };
    isExternalLink: {
      value: boolean;
    };
  };
  agreement?: number;
}

interface FormCreateModalHandleOptions {
  showBenefitsSelector?: boolean;
  benefit?: {
    title: string;
    category: string;
    image: string;
    url?: string;
  } | null;
}

interface FormCreateModalHandle {
  open: (data?: ModuleProduct, options?: FormCreateModalHandleOptions) => void;
  close: () => void;
}

interface FormCreateProps {
  createRef: React.MutableRefObject<FormCreateModalHandle | null>;
  refetch: () => void | Promise<unknown>;
  existingModules?: any[];
  isModuleFormat?: boolean;
}

export function FormCreate({ createRef, refetch, existingModules = [], isModuleFormat = false }: FormCreateProps) {
  const { colors, id } = useTheme();
  const queryClient = useQueryClient();

  const {
    reset,
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
    clearErrors,
  } = useForm<ModuleProductSubmit>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      default_product: '',
      is_external: true,
      metadata: {
        url: '',
        logo: '',
        category: '',
        isExternalLink: true,
      },
      legal_terms: false,
    },
  });

  const [loading, setLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [toggleActiveLoading, setToggleActiveLoading] = React.useState(false);
  // const [valueImage, setValueImage] = React.useState<string | undefined>();
  const [currentData, setCurrentData] = React.useState<ModuleProduct | null>(null);
  const [selectedBenefit, setSelectedBenefit] = React.useState<any | null>(null);
  const [hasImage, setHasImage] = React.useState(false);
  const [customSegments, setCustomSegments] = React.useState<string[]>([]);
  const [showBenefitsSelector, setShowBenefitsSelector] = React.useState(true);
  const [lockedCategory, setLockedCategory] = React.useState<string | null>(null);
  const [isCategoryLocked, setIsCategoryLocked] = React.useState(false);
  const [allowCategoryLock, setAllowCategoryLock] = React.useState(false);

  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const urlPreviousValueRef = React.useRef<string>('');

  const handleCreate = useDisclosure();
  const handleDelete = useDisclosure();

  const name = watch('name');
  const logo = watch('metadata.logo');
  const category = watch('metadata.category');
  const url = watch('metadata.url');

  const toast = useToast();

  // Validar se o formulário está completo
  const isFormValid = React.useMemo(() => {
    const formValid = name?.trim() && category?.trim() && url?.trim() && hasImage && isValid;

    return formValid;
  }, [name, category, url, hasImage, isValid]);


  // Atualizar estado da imagem quando mudar
  useEffect(() => {
    setHasImage(!!logo);
    if (logo) {
      clearErrors('metadata.logo');
    }
  }, [logo, clearErrors]);

  // Sincronizar a ref com o valor da URL quando mudar externamente
  useEffect(() => {
    if (url !== undefined && url !== null) {
      urlPreviousValueRef.current = url;
    }
  }, [url]);

  const handleCreateModuleFormat = async (formData: {
    title: string;
    section_title: string;
    url: string;
    is_external: boolean;
    image: File | string;
    legal_terms: boolean;
    id?: number;
    agreement?: number;
  }) => {
    try {
      setLoading(true);
      // Adapted from file_context_1:
      // This handles the alternate "module format" (not product SVA) using its own fields
      // (title, section_title, url, is_external, image)
      // The backend expects icon for the image.
      // Additionally, for edit, PATCH with the id; for new, POST to the correct endpoint.

      // For debug
      console.log('FormData completo:', formData);
      console.log('Categoria:', formData.section_title);
      console.log('Termo jurídico aceito:', formData.legal_terms);

      const data = new FormData();

      // Required fields
      data.append('title', (formData.title || '').trim());
      data.append('section_title', (formData.section_title || '').trim());
      data.append('active', 'true');
      data.append('url', (formData.url || '').trim());

      // Handle is_external
      if (formData.is_external !== undefined) {
        data.append('is_external', String(formData.is_external));
      }

      // Handle image as icon
      if (formData.image && typeof formData.image !== 'string') {
        data.append('icon', formData.image);
      }

      if (currentData?.id) {
        // Editing existing module
        await axiosInstance.patch(`/agreement_staff/modules/${currentData.id}/`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast({
          title: 'Módulo editado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Creating a new module
        if (id) {
          data.append('agreement', id.toString());
        }

        await axiosInstance.post('/agreement_staff/modules/', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast({
          title: 'Módulo criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      refetch();
      handleCreate.onClose();
      resetForm();

      queryClient.refetchQueries([DashboardHooks.Modules]);
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);
    } catch (error: any) {
      console.error('Error creating/updating module:', error);

      let errorMessage = 'Erro inesperado';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.title) {
        errorMessage = error.response.data.title[0] || 'Erro no título';
      } else if (error.response?.data?.url) {
        errorMessage = error.response.data.url[0] || 'Erro na URL';
      } else if (error.response?.data?.section_title) {
        errorMessage = error.response.data.section_title[0] || 'Erro na categoria';
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique os campos e tente novamente.';
      } else if (error.response?.status === 413) {
        errorMessage = 'Imagem muito grande. Selecione uma imagem menor.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.';
      }

      toast({
        title: 'Erro',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewModule = async (formData: ModuleProductSubmit) => {
    try {

      if(!currentData?.id && existingModules.find(module => module.name?.toLowerCase() === formData.name.trim().toLowerCase())) {
        toast({
          title: 'Já existe um módulo com este nome',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if(isModuleFormat) {
        handleCreateModuleFormat({
          title: formData.name.trim(),
          section_title: formData.metadata.category.trim(),
          url: formData.metadata.url.trim(),
          is_external: formData.is_external,
          image: formData.metadata.logo,
          legal_terms: formData.legal_terms,
          id: currentData?.id,
          agreement: id || undefined,
        });
        return;
      }
      setLoading(true);

      const data = new FormData();

      data.append('name', formData.name.trim());
      data.append('default_product', '4');
      data.append('active', 'true');
      data.append('metadata.url', formData.metadata.url.trim());
      data.append('metadata.category', formData.metadata.category.trim());
      data.append('metadata.isExternalLink', String(formData.metadata.isExternalLink));

      // Handle logo properly - backend expects 'logo' field
      if (formData.metadata.logo && typeof formData.metadata.logo !== 'string') {
        data.append('metadata.logo', formData.metadata.logo);
      }

      if (currentData?.id) {
        await axiosInstance.patch(`/product_staff/${currentData.id}/`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast({
          title: 'Módulo editado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // CRIAR
        if (id) {
          data.append('agreement', id.toString());
        }

        data.append('default_product', '4');
        await axiosInstance.post('/product_staff/', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast({
          title: 'Módulo criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      refetch();
      handleCreate.onClose();
      resetForm();

      queryClient.refetchQueries([DashboardHooks.Modules]);
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);
    } catch (error: any) {
      console.error('Error creating/updating module:', error);

      let errorMessage = 'Erro inesperado';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.name) {
        errorMessage = error.response.data.name[0] || 'Erro no nome';
      } else if (error.response?.data?.url) {
        errorMessage = error.response.data.url[0] || 'Erro na URL';
      } else if (error.response?.data?.category) {
        errorMessage = error.response.data.category[0] || 'Erro na categoria';
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique os campos e tente novamente.';
      } else if (error.response?.status === 413) {
        errorMessage = 'Imagem muito grande. Selecione uma imagem menor.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.';
      }

      toast({
        title: 'Erro',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!currentData?.id) return;

    try {
      setToggleActiveLoading(true);
      const newActive = !currentData.active;
      if (isModuleFormat) {
        await axiosInstance.patch(`/agreement_staff/modules/${currentData.id}/`, { active: newActive });
      } else {
        await axiosInstance.patch(`/product_staff/${currentData.id}/`, { active: newActive });
      }
      setCurrentData((prev) => (prev ? { ...prev, active: newActive } : null));
      await queryClient.refetchQueries([DashboardHooks.Products]);
      await queryClient.refetchQueries([DashboardHooks.Modules]);
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);
      const refetchResult = refetch();
      if (refetchResult && typeof refetchResult.then === 'function') {
        await refetchResult;
      }
      toast({
        title: newActive ? 'Módulo habilitado' : 'Módulo desabilitado',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar',
        description: error?.response?.data?.detail || 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setToggleActiveLoading(false);
    }
  };

  const handleDeleteModule = async () => {
    if (!currentData?.id) return;

    try {
      setDeleteLoading(true);

      if(isModuleFormat) {
        await axiosInstance.delete(`/agreement_staff/modules/${currentData.id}/`);
      } else {
        await axiosInstance.delete(`/product_staff/${currentData.id}/`);
      }

      toast({
        title: 'Módulo deletado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetch();
      handleCreate.onClose();
      resetForm();

      queryClient.refetchQueries([DashboardHooks.Modules]);
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);
    } catch (error: any) {
      console.error('Error deleting module:', error);

      let errorMessage = 'Erro ao deletar módulo';
      if (error.response?.status === 404) {
        errorMessage = 'Módulo não encontrado';
      } else if (error.response?.status === 403) {
        errorMessage = 'Sem permissão para deletar este módulo';
      }

      toast({
        title: 'Erro',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
      handleDelete.onClose();
    }
  };

  const resetFormFields = useCallback(() => {
    reset();
    setCurrentData(null);
    setSelectedBenefit(null);
    setHasImage(false);
    setValue('legal_terms', false);
    clearErrors();
  }, [reset, clearErrors, setValue]);

  const resetForm = useCallback(() => {
    resetFormFields();
    setLockedCategory(null);
    setIsCategoryLocked(false);
    setAllowCategoryLock(false);
  }, [resetFormFields]);

  const handleAddCustomSegment = useCallback((segment: string) => {
    setCustomSegments(prev => {
      if (!prev.includes(segment)) {
        return [...prev, segment];
      }
      return prev;
    });
  }, []);

  const handleRemoveCustomSegment = useCallback((segment: string) => {
    setCustomSegments(prev => prev.filter(s => s !== segment));
  }, []);

  const handleSend = async (formData: ModuleProductSubmit) => {
    if (!formData.name?.trim()) {
      toast({
        title: 'Erro',
        description: 'O nome do app é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validar se já existe um módulo com o mesmo nome
    const trimmedName = formData.name.trim();
    const existingModule = existingModules.find(
      (module) =>
        module.name?.toLowerCase() === trimmedName.toLowerCase() &&
        module.id !== currentData?.id
    );

    if (existingModule) {
      toast({
        title: 'Erro',
        description: 'Já existe um benefício com este nome. Escolha um nome diferente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.metadata.category?.trim()) {
      toast({
        title: 'Erro',
        description: 'A categoria é obrigatória',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.metadata.url?.trim()) {
      toast({
        title: 'Erro',
        description: 'O link de redirecionamento é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.metadata.logo) {
      toast({
        title: 'Erro',
        description: 'É necessário adicionar uma imagem',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Basic URL validation
    if (!formData.metadata.url.startsWith('http://') && !formData.metadata.url.startsWith('https://') && !formData.metadata.url.startsWith('/')) {
      toast({
        title: 'Erro',
        description: 'URL deve começar com http://, https:// ou / para links relativos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Trigger validation antes de enviar
    const isValid = await trigger();
    if (!isValid) {
      toast({
        title: 'Erro',
        description: 'Por favor, corrija os erros no formulário',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    handleCreateNewModule(formData);
  };

  const openModal = useCallback(
    (data?: ModuleProduct, options?: FormCreateModalHandleOptions) => {
      resetForm();
      console.log('data', data);
      const rawCategory =
        data?.sectionTitle ||
        (data as any)?.metadata?.category?.value ||
        data?.category ||
        '';

      const normalizedCategory = rawCategory?.trim() || '';
      const shouldAutoLockCategory =
        !!normalizedCategory && !options?.benefit;

      const hasCustomShowSelectorOption =
        options && Object.prototype.hasOwnProperty.call(options, 'showBenefitsSelector');

      if (hasCustomShowSelectorOption && !shouldAutoLockCategory) {
        setShowBenefitsSelector(!!options?.showBenefitsSelector);
      } else {
        setShowBenefitsSelector(!shouldAutoLockCategory);
      }

      console.log('shouldAutoLockCategory', shouldAutoLockCategory, 'normalizedCategory', normalizedCategory, 'options?.benefit', options?.benefit);
      if (shouldAutoLockCategory) {
        setAllowCategoryLock(true);
        setIsCategoryLocked(true);
        setLockedCategory(normalizedCategory);
        setValue('metadata.category', normalizedCategory, { shouldValidate: true });
      } else {
        setAllowCategoryLock(false);
        setIsCategoryLocked(false);
        setLockedCategory(null);
      }

      handleCreate.onOpen();

      // Limpar seleção de benefício sempre que abrir o modal
      setSelectedBenefit(null);

      // Se um benefício padrão foi passado (catálogo), usar o fluxo existente do BenefitsSelector
      if (options?.benefit) {
        const benefit = options.benefit;
        setCurrentData(null);
        resetForm();

        // Preencher campos básicos
        if (benefit.title) {
          setValue('name', benefit.title, { shouldValidate: true });
        }
        if (benefit.url) {
          setValue('metadata.url', benefit.url, { shouldValidate: true });
        }
        if (benefit.category) {
          setValue('metadata.category', benefit.category, { shouldValidate: true });
        }
        // isExternalLink padrão para benefícios do catálogo
        setValue('metadata.isExternalLink', true, { shouldValidate: true });

        // Baixar e setar imagem como File, reproduzindo comportamento do BenefitsSelector
        if (benefit.image) {
          fetch(benefit.image)
            .then((response) => response.blob())
            .then((blob) => {
              const fileName = `${benefit.title.toLowerCase().replace(/\s+/g, '-')}-icon`;
              const fileExtension = benefit.image.split('.').pop() || 'png';
              const file = new File([blob], `${fileName}.${fileExtension}`, {
                type: blob.type || 'image/png',
              });
              setValue('metadata.logo', file, { shouldValidate: true });
              setHasImage(true);
            })
            .catch((error) => {
              console.error('Erro ao carregar imagem do benefício padrão:', error);
            });
        }

        return;
      }


      if (data?.id) {
        setCurrentData(data);

        if (isModuleFormat) {
          // Edição no formato de módulo (ModulesSuperApp)
          setValue('name', data.name || data.title || '');

          // URL simples no formato de módulo
          setValue('metadata.url', (data as any).metadata?.url?.value || data.url || '');

          // Categoria: sectionTitle (módulo) ou metadata.category/value
          const categoryValue =
            data.sectionTitle ||
            (data as any).metadata?.category?.value ||
            data.category ||
            '';

          setValue('metadata.category', categoryValue);

          // Logo: priorizar icon, depois image, depois metadata.logo.value
          const logoValue =
            data.icon ||
            data.image ||
            ((data as any).metadata && (data as any).metadata.logo && (data as any).metadata.logo.value) ||
            '';
          setValue('metadata.logo', logoValue as any);
          setHasImage(!!logoValue);

          // isExternalLink: metadata.isExternalLink.value (mesmo lugar que url e category)
          const isExternalLinkValue = (data as any).metadata?.isExternalLink?.value ?? (data as any).is_external ?? data.isExternal ?? false;
          setValue('metadata.isExternalLink', isExternalLinkValue);
          setValue('is_external', isExternalLinkValue);
        } else {
          // Edição no formato de produto SVA (ProductsAndPlans)
          setValue('name', data.name || data.title || '');
          setValue('default_product', String(data.default_product?.id || ''));
          setValue('metadata.url', data.metadata?.url?.value || data.url || '');
          setValue('legal_terms', true); // Em edição, assume que já aceitou os termos

          // Logo: metadata.logo.value ou icon
          const logoValue = (data.metadata?.logo?.value || data.icon || '') as any;
          setValue('metadata.logo', logoValue);
          setHasImage(!!logoValue);

          // Categoria: metadata.category.value ou category
          const categoryValue =
            data.metadata?.category?.value ||
            data.category ||
            data.sectionTitle ||
            '';
          setValue('metadata.category', categoryValue);

          // isExternalLink: metadata.isExternalLink.value (mesmo lugar que url e category)
          const isExternalLinkValue = data.metadata?.isExternalLink?.value ?? (data as any).is_external ?? data.isExternal ?? false;
          setValue('metadata.isExternalLink', isExternalLinkValue);
          setValue('is_external', isExternalLinkValue);
        }
      }
    },
    [setValue, handleCreate, resetForm, resetFormFields, isModuleFormat]
  );

  const closeModal = useCallback(() => {
    handleCreate.onClose();
    resetForm();
    setShowBenefitsSelector(true);
  }, [handleCreate, resetForm]);

  const handleBenefitSelect = useCallback(
    async (benefit: any) => {
      setSelectedBenefit(benefit);

      // Se benefício for null (deseleção), limpar os campos
      if (!benefit) {
        setValue('name', '', { shouldValidate: true });
        setValue('metadata.url', '', { shouldValidate: true });
        setValue('metadata.category', '', { shouldValidate: true });
        setValue('metadata.logo', '', { shouldValidate: true });
        setValue('metadata.isExternalLink', true, { shouldValidate: true });
        setHasImage(false);
        return;
      }

      try {
        // Preencher automaticamente nome e link
        if (benefit.title) {
          setValue('name', benefit.title, { shouldValidate: true });
        }
        if (benefit.url) {
          setValue('metadata.url', benefit.url, { shouldValidate: true });
        }
        if (benefit.category) {
          setValue('metadata.category', benefit.category, { shouldValidate: true });
        }
        // isExternalLink padrão para benefícios do catálogo
        setValue('metadata.isExternalLink', true, { shouldValidate: true });

        // Converter URL da imagem para File
        if (benefit.image) {
          const response = await fetch(benefit.image);
          const blob = await response.blob();
          const fileName = `${benefit.title.toLowerCase().replace(/\s+/g, '-')}-icon`;
          const fileExtension = benefit.image.split('.').pop() || 'png';
          const file = new File([blob], `${fileName}.${fileExtension}`, {
            type: blob.type || 'image/png',
          });
          setValue('metadata.logo', file, { shouldValidate: true });
          setHasImage(true);
        }

        toast({
          title: 'Benefício selecionado',
          description: `${benefit.title} foi selecionado com sucesso`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Erro ao selecionar benefício:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível selecionar o benefício',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [setValue, toast]
  );

  const handleEditLockedCategory = useCallback(() => {
    setIsCategoryLocked(false);
    setShowBenefitsSelector(true);
  }, []);

  const handleSaveLockedCategory = useCallback(() => {
    const trimmedCategory = category?.trim();

    if (!trimmedCategory) {
      toast({
        title: 'Categoria obrigatória',
        description: 'Selecione ou informe uma categoria antes de salvar.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLockedCategory(trimmedCategory);
    setValue('metadata.category', trimmedCategory, { shouldValidate: true });
    setIsCategoryLocked(true);
    setShowBenefitsSelector(false);
  }, [category, toast, setValue]);

  useImperativeHandle(
    createRef,
    () => ({
      open: openModal,
      close: closeModal,
    }),
    [openModal, closeModal]
  );

  return (
    <>
      <Modal isOpen={handleCreate.isOpen} onClose={closeModal} size={{ base: '3xl', lg: '6xl' }} closeOnOverlayClick={!loading}>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="20px" maxH="90vh" mx={4} overflow="hidden" boxShadow="2xl">
          <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3} fontSize="lg" fontWeight="bold">
            {currentData?.id ? 'Editar' : 'Criar novo'} Sva
          </ModalHeader>
          <ModalCloseButton
            isDisabled={loading}
            top={3}
            right={3}
            size="md"
            borderRadius="full"
            _hover={{ bg: 'gray.100' }}
          />

          <Box
            maxH="calc(90vh - 140px)"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#e2e8f0',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#cbd5e0',
              },
            }}
          >
            <VStack width="100%" padding="20px" spacing={2}>
              <form style={{ width: '100%' }} onSubmit={handleSubmit(handleSend)}>
                <Stack
                  direction={{ base: 'column', lg: 'row' }}
                  spacing={5}
                  align="stretch"
                  width="100%"
                >
                  {/* Coluna esquerda: catálogo de SVAs */}
                  {showBenefitsSelector && (!allowCategoryLock || !isCategoryLocked) && (
                    <Box
                      flex={{ base: '1', lg: '0 0 42%' }}
                      maxW={{ base: '100%', lg: '42%' }}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="14px"
                      bg="gray.50"
                      p={3}
                      alignSelf="flex-start"
                      position={{ lg: 'sticky' }}
                      top={{ lg: 0 }}
                      maxH={{ lg: 'calc(90vh - 200px)' }}
                      overflowY={{ lg: 'auto' }}
                      css={{
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '3px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#cbd5e0' },
                      }}
                    >
                      <HStack spacing={2} mb={2} align="center">
                        <Box
                          width="22px"
                          height="22px"
                          borderRadius="full"
                          bg="blue.500"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          1
                        </Box>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800">
                          Escolha um SVA do catálogo
                        </Text>
                      </HStack>

                      <BenefitsSelector
                        onSelectBenefit={handleBenefitSelect}
                        selectedBenefit={selectedBenefit}
                        onCategoryChange={(category) => {
                          setValue('metadata.category', category, { shouldValidate: true });
                        }}
                        currentCategory={category}
                        existingModules={existingModules}
                        customSegments={customSegments}
                        onAddCustomSegment={handleAddCustomSegment}
                        onRemoveCustomSegment={handleRemoveCustomSegment}
                      />

                      {allowCategoryLock && (
                        <HStack justifyContent="flex-end" mt={3}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="solid"
                            onClick={handleSaveLockedCategory}
                            type="button"
                          >
                            Salvar categoria
                          </Button>
                        </HStack>
                      )}
                    </Box>
                  )}

                  {/* Coluna direita: configuração do benefício */}
                  <VStack
                    flex="1"
                    spacing={3}
                    align="stretch"
                    width="100%"
                    maxW={
                      showBenefitsSelector && (!allowCategoryLock || !isCategoryLocked)
                        ? undefined
                        : '640px'
                    }
                    mx={
                      showBenefitsSelector && (!allowCategoryLock || !isCategoryLocked)
                        ? undefined
                        : 'auto'
                    }
                  >
                    <HStack spacing={2} align="center" width="100%">
                      <Box
                        width="22px"
                        height="22px"
                        borderRadius="full"
                        bg="blue.500"
                        color="white"
                        fontSize="xs"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        2
                      </Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        Configure o benefício
                      </Text>
                    </HStack>

                  {currentData?.id && (
                    <FormControl display="flex" alignItems="center" width="100%" pb={2} borderBottom="1px solid" borderColor="gray.100">
                      <FormLabel htmlFor="active-switch" mb={0} fontSize="sm" fontWeight="medium">
                        Módulo ativo
                      </FormLabel>
                      <Switch
                        id="active-switch"
                        isChecked={currentData.active !== false}
                        onChange={handleToggleActive}
                        isDisabled={toggleActiveLoading}
                        colorScheme="green"
                        size="md"
                      />
                    </FormControl>
                  )}

                  {allowCategoryLock && isCategoryLocked && (
                    <Box
                      width="100%"
                      border="1px solid"
                      borderColor="blue.100"
                      borderRadius="10px"
                      backgroundColor="blue.50"
                      p={3}
                    >
                      <HStack justifyContent="space-between" alignItems="flex-start" spacing={3}>
                        <VStack align="flex-start" spacing={0.5}>
                          <Text fontSize="xs" fontWeight="semibold" color="blue.700">
                            Categoria selecionada
                          </Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.800">
                            {lockedCategory || category || 'Categoria não definida'}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            Clique em editar para alterar esta categoria.
                          </Text>
                        </VStack>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={handleEditLockedCategory}
                          type="button"
                        >
                          Editar categoria
                        </Button>
                      </HStack>
                    </Box>
                  )}

                  {/* Preview da marca - sempre visível */}
                  <Box
                    mt={0}
                    p={4}
                    border="1px solid"
                    borderColor={selectedBenefit || logo ? 'gray.200' : 'red.300'}
                    borderRadius="12px"
                    backgroundColor={selectedBenefit || logo ? 'gray.50' : 'red.50'}
                  >
                    <VStack spacing={3}>
                      <Text fontSize="sm" fontWeight="semibold" color={selectedBenefit || logo ? 'gray.800' : 'red.700'}>
                        {selectedBenefit || logo ? 'Preview da marca selecionada' : 'Adicionar marca *'}
                      </Text>
                      {!selectedBenefit && !logo && (
                        <Text color="red.500" fontSize="xs" mt={-2}>
                          Imagem é obrigatória
                        </Text>
                      )}

                      <HStack spacing={4} align="center" flexDirection={{
                        base: 'column',
                        md: 'row',
                      }}>
                        {/* Preview da imagem */}
                        <Box
                          width="80px"
                          height="80px"
                          borderRadius="12px"
                          overflow="hidden"
                          backgroundColor="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          boxShadow="md"
                          border={selectedBenefit || logo ? '2px solid' : '2px dashed'}
                          borderColor={selectedBenefit || logo ? 'blue.200' : 'red.300'}
                          cursor={!selectedBenefit && !logo ? 'pointer' : 'default'}
                          _hover={!selectedBenefit && !logo ? {
                            backgroundColor: 'red.50',
                            borderColor: 'red.400',
                          } : {}}
                          transition="all 0.2s"
                          onClick={() => {
                            if (!selectedBenefit && !logo) {
                              const fileInput = document.createElement('input');
                              fileInput.type = 'file';
                              fileInput.accept = 'image/*';
                              fileInput.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) {
                                  setValue('metadata.logo', file, { shouldValidate: true });
                                  setHasImage(true);
                                  setSelectedBenefit(null);
                                  toast({
                                    title: 'Imagem adicionada',
                                    description: 'Sua marca foi carregada com sucesso',
                                    status: 'success',
                                    duration: 2000,
                                    isClosable: true,
                                  });
                                }
                              };
                              fileInput.click();
                            }
                          }}
                        >
                          {logo && typeof logo === 'string' ? (
                            <Image
                              src={logo}
                              alt="Preview da marca"
                              width="100%"
                              height="100%"
                              objectFit="cover"
                            />
                          ) : logo && typeof logo === 'object' ? (
                            <Image
                              src={URL.createObjectURL(logo)}
                              alt="Preview da marca"
                              width="100%"
                              height="100%"
                              objectFit="cover"
                            />
                          ) : selectedBenefit?.image ? (
                            <Image
                              src={selectedBenefit.image}
                              alt={selectedBenefit.title}
                              width="100%"
                              height="100%"
                              objectFit="cover"
                            />
                          ) : (
                            <VStack spacing={1} alignItems="center">
                              <Box
                                width="24px"
                                height="24px"
                                borderRadius="6px"
                                backgroundColor="red.100"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="14px"
                                color="red.600"
                                fontWeight="bold"
                              >
                                +
                              </Box>
                              <Text fontSize="10px" color="red.600" textAlign="center">
                                Adicionar
                              </Text>
                            </VStack>
                          )}
                        </Box>

                        {/* Informações do benefício */}
                        <VStack align="start" spacing={1} flex={1}>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {selectedBenefit?.title || 'Marca personalizada'}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {selectedBenefit?.category || category || 'Categoria não definida'}
                          </Text>
                          {selectedBenefit?.url && (
                            <Text fontSize="xs" color="blue.600" noOfLines={1}>
                              {selectedBenefit.url}
                            </Text>
                          )}
                          {!selectedBenefit && !logo && (
                            <Text fontSize="xs" color="red.500" fontStyle="italic">
                              Clique no ícone ou selecione um benefício
                            </Text>
                          )}
                        </VStack>

                        {/* Botão para alterar imagem */}
                        {(selectedBenefit || logo) && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            onClick={() => {
                              const fileInput = document.createElement('input');
                              fileInput.type = 'file';
                              fileInput.accept = 'image/*';
                              fileInput.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) {
                                  setValue('metadata.logo', file, { shouldValidate: true });
                                  setHasImage(true);
                                  setSelectedBenefit(null);
                                  toast({
                                    title: 'Imagem alterada',
                                    description: 'A imagem foi atualizada com sucesso',
                                    status: 'success',
                                    duration: 2000,
                                    isClosable: true,
                                  });
                                }
                              };
                              fileInput.click();
                            }}
                            _hover={{
                              transform: 'translateY(-1px)',
                              boxShadow: 'md',
                            }}
                            transition="all 0.2s"
                          >
                            Alterar imagem
                          </Button>
                        )}
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Inputs lado a lado e compactos */}
                  <Stack direction={{ base: 'column', md: 'row' }} width="100%" spacing={3} align="flex-start">
                    <Labeled label="Nome do App" labelSize={14} width="100%" flex={1}>
                      <Input
                        placeholder="Digite o nome do aplicativo"
                        isInvalid={!!errors.name}
                        size="md"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor="gray.200"
                        fontSize="sm"
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                        }}
                        _hover={{
                          borderColor: 'gray.300',
                        }}
                        {...register('name', {
                          required: 'Nome do app é obrigatório',
                          minLength: {
                            value: 2,
                            message: 'Nome deve ter pelo menos 2 caracteres',
                          },
                        })}
                      />
                      {errors.name && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {errors.name.message}
                        </Text>
                      )}
                    </Labeled>

                    <Labeled label={'Link de redirecionamento'}
                      labelSize={14}
                      width="100%"
                      flex={1}
                    >
                      <Input
                        placeholder="https://exemplo.com ou /rota"
                        isInvalid={!!errors.metadata?.url}
                        size="md"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor="gray.200"
                        fontSize="sm"
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                        }}
                        _hover={{
                          borderColor: 'gray.300',
                        }}
                        onFocus={(e) => {
                          const currentValue = e.target.value;
                          // Atualizar a ref com o valor atual
                          urlPreviousValueRef.current = currentValue;
                          if (!currentValue) {
                            setValue('metadata.url', 'https://', { shouldValidate: false });
                            urlPreviousValueRef.current = 'https://';
                            // Mover o cursor para o final após adicionar https://
                            setTimeout(() => {
                              e.target.setSelectionRange(8, 8);
                            }, 0);
                          }
                        }}
                        {...(() => {
                          const { onChange, ...rest } = register('metadata.url', {
                            required: 'URL é obrigatória',
                            pattern: {
                              value: /^(https?:\/\/|\/)/,
                              message: 'URL deve começar com http://, https:// ou /',
                            },
                          });
                          return {
                            ...rest,
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                              const currentValue = e.target.value;
                              const previousValue = urlPreviousValueRef.current;

                              // Detectar se o usuário está apagando (valor diminuindo)
                              const isDeleting = currentValue.length < previousValue.length;

                              // Se o usuário está apagando, permitir que apague normalmente
                              if (isDeleting) {
                                urlPreviousValueRef.current = currentValue;
                                onChange(e);
                                return;
                              }

                              // Se o campo está vazio ou só tem espaços, não fazer nada (será tratado no onFocus)
                              if (!currentValue.trim()) {
                                urlPreviousValueRef.current = currentValue;
                                onChange(e);
                                return;
                              }

                              // Se já começa com http://, https:// ou /, permitir normalmente
                              if (currentValue.match(/^(https?:\/\/|\/)/)) {
                                urlPreviousValueRef.current = currentValue;
                                onChange(e);
                                return;
                              }

                              // Se o usuário está digitando algo que não começa com http://, https:// ou /, adicionar https://
                              // Mas só se não estiver apagando e o valor não estiver vazio
                              if (currentValue && !isDeleting) {
                                const newValue = `https://${currentValue}`;
                                urlPreviousValueRef.current = newValue;
                                setValue('metadata.url', newValue, { shouldValidate: false });
                                // Mover o cursor para o final
                                setTimeout(() => {
                                  e.target.setSelectionRange(newValue.length, newValue.length);
                                }, 0);
                              } else {
                                urlPreviousValueRef.current = currentValue;
                                onChange(e);
                              }
                            },
                          };
                        })()}
                      />
                      {errors.metadata?.url && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {errors.metadata.url.message}
                        </Text>
                      )}
                    </Labeled>
                  </Stack>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <FormLabel htmlFor="is_external" mb="0" fontSize="sm" fontWeight="medium">
                      Link externo
                    </FormLabel>
                    <Switch
                      id="is_external"
                      {...(() => {
                        const { onChange, ...rest } = register('is_external');
                        return {
                          ...rest,
                          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.checked;
                            onChange(e);
                            setValue('metadata.isExternalLink', value, { shouldValidate: true });
                          },
                        };
                      })()}
                      colorScheme="blue"
                      size="md"
                    />
                  </FormControl>

                  <Labeled label={''}>
                    <Checkbox
                      {...register('legal_terms', {
                        required: 'Aceite dos termos jurídicos é obrigatório',
                      })}
                      size="md"
                      colorScheme="blue"
                      _hover={{ transform: 'scale(1.02)' }}
                      transition="all 0.2s"
                    >
                      <Text textTransform={'none'} fontSize="sm" lineHeight="1.4">
                        A Partiu não possui contrato com aplicativos acima sejam eles streaming, TVs, Telemicinas ou outros. Declaro que possuo contrato de fornecimento vigente, com autorização para distribuição e direito
                        de uso da marca em comunicações
                      </Text>
                    </Checkbox>
                    {errors.legal_terms && (
                      <Text color="red.500" fontSize="xs" mt={1}>
                        {errors.legal_terms.message}
                      </Text>
                    )}
                  </Labeled>

                  {/* Removido: upload de imagem no modal. Agora feito na lista de benefícios. */}
                  </VStack>
                </Stack>
              </form>
            </VStack>
          </Box>

          <ModalFooter borderTop="1px solid" borderColor="gray.100" pt={3} px={5} pb={5}>
            <HStack width={'100%'} justifyContent={'space-between'}>
              {currentData?.id ? (
                <Button
                  colorScheme="red"
                  variant="outline"
                  isDisabled={loading}
                  size="md"
                  borderRadius="8px"
                  onClick={() => {
                    handleDelete.onOpen();
                  }}
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'md',
                  }}
                  transition="all 0.2s"
                >
                  Deletar
                </Button>
              ) : (
                <div></div>
              )}
              <HStack spacing={3}>
                <Button
                  variant="ghost"
                  isDisabled={loading}
                  onClick={closeModal}
                  size="md"
                  borderRadius="8px"
                  fontSize="sm"
                  _hover={{
                    bg: 'gray.100',
                    transform: 'translateY(-1px)',
                  }}
                  transition="all 0.2s"
                >
                  Cancelar
                </Button>

                <Button
                  _hover={{
                    backgroundColor: colors.primaryColor,
                    transform: 'translateY(-1px)',
                    boxShadow: 'md',
                  }}
                  color="white"
                  isLoading={loading}
                  isDisabled={!isFormValid}
                  loadingText={currentData?.id ? 'Editando...' : 'Criando...'}
                  onClick={() => handleSubmit(handleSend)()}
                  backgroundColor={colors.primaryColor}
                  size="md"
                  borderRadius="8px"
                  fontSize="sm"
                  transition="all 0.2s"
                >
                  {currentData?.id ? 'Editar' : 'Criar'}
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={handleDelete.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleDelete.onClose}
        closeOnOverlayClick={!deleteLoading}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deletar Módulo
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja deletar o módulo "{currentData?.name}"? Você não pode desfazer esta ação depois.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleDelete.onClose} isDisabled={deleteLoading}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteModule}
                ml={3}
                isLoading={deleteLoading}
                loadingText="Deletando..."
              >
                Deletar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
